import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { AnimateIn } from '@oysterjs/ui/transition';

import { type CaraWorkspaceFieldsFragment } from '@oysterjs/ais/api/graphql';

type Stage = CaraWorkspaceFieldsFragment['planStages'][0];

const InProgressDot = () => (
  <span className="relative inline-flex size-2 shrink-0">
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-300 dark:bg-blue-500/30 opacity-75" />
    <span className="relative inline-flex size-2 rounded-full bg-blue-500" />
  </span>
);

/**
 * Shown while a ProcessAttachments stage is in progress. Renders a compact
 * card with a per-file progress bar for each attachment being processed.
 *
 * Completed stages produce no stage-level entry so their events fold
 * seamlessly into the surrounding StageEventGroup.
 */
export const ProcessAttachmentsStage = (props: { stage: Stage }) => {
  const { stage } = props;
  const files = stage.processAttachmentsDetails?.progress ?? [];
  const process = (100 * files.reduce((acc, file) => acc + (file.progress ?? 0), 0)) / files.length;

  return (
    <AnimateIn data-conversation-role="stage-event" exit={{ opacity: 0, y: -6 }}>
      <div className="flex min-w-0 items-center gap-2">
        <InProgressDot />
        <div className="relative min-w-0 flex-1 overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              className={clsx(
                'block truncate text-left text-sm',
                'font-medium text-neutral-700 dark:text-neutral-300'
              )}
            >
              Processing attachments
            </motion.span>
          </AnimatePresence>
        </div>
      </div>

      <div className="ml-4 mt-2 flex flex-col gap-y-1 max-w-96">
        <div className="h-2 w-full rounded-full bg-neutral-200/80 dark:bg-neutral-700">
          <div
            className={`h-2 rounded-full bg-gradient-to-r to-primary-300 dark:to-primary-500/30 from-primary-600 bg-[size:200%_100%] animate-progress-bar transition-all ease-in-out duration-300`}
            style={{
              width: `${process}%`
            }}
          />
        </div>
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          Uploading {files.length} file{files.length !== 1 ? 's' : ''} ({process.toFixed(0)}%)...
        </div>
      </div>
    </AnimateIn>
  );
};
